/* VENA — custom header interactions for Dawn */
(() => {
  'use strict';

  const SELECTORS = {
    root: '[data-vena-header-root]',
    menuToggle: '[data-vena-menu-toggle]',
    menuDrawer: '[data-vena-mobile-drawer]',
    menuScrim: '[data-vena-menu-scrim]',
    searchToggle: '[data-vena-search-toggle]',
    searchOverlay: '[data-vena-search-overlay]',
    searchDialog: '[data-vena-search-dialog]',
    searchClose: '[data-vena-search-close]',
    searchInput: '[data-vena-search-input]',
    searchResults: '[data-vena-search-results]',
    quickLink: '[data-vena-quick-link]'
  };

  const escapeHTML = (value = '') => String(value)
    .replace(/&/g, '&amp;')
    .replace(/</g, '&lt;')
    .replace(/>/g, '&gt;')
    .replace(/"/g, '&quot;')
    .replace(/'/g, '&#039;');

  const debounce = (callback, delay = 260) => {
    let timeout;
    return (...args) => {
      window.clearTimeout(timeout);
      timeout = window.setTimeout(() => callback(...args), delay);
    };
  };

  function setMenuState(root, isOpen) {
    const drawer = root.querySelector(SELECTORS.menuDrawer);
    const toggle = root.querySelector(SELECTORS.menuToggle);
    const scrim = root.querySelector(SELECTORS.menuScrim);
    if (!drawer || !toggle) return;

    drawer.toggleAttribute('open', isOpen);
    drawer.setAttribute('aria-hidden', isOpen ? 'false' : 'true');
    toggle.setAttribute('aria-expanded', isOpen ? 'true' : 'false');
    toggle.setAttribute('aria-label', isOpen ? 'Close menu' : 'Open menu');
    if (isOpen && scrim) {
      const headerBottom = Math.max(0, root.getBoundingClientRect().bottom);
      scrim.style.top = `${headerBottom}px`;
    }
    scrim?.classList.toggle('is-active', isOpen);
    document.body.classList.toggle('vena-menu-is-open', isOpen);

    if (isOpen) {
      window.setTimeout(() => drawer.querySelector('a, button')?.focus(), 120);
    }
  }

  function closeDrawer(root) {
    setMenuState(root, false);
  }

  function setOverlayState(overlay, isOpen) {
    const input = overlay.querySelector(SELECTORS.searchInput);
    overlay.classList.toggle('is-active', isOpen);
    overlay.setAttribute('aria-hidden', isOpen ? 'false' : 'true');
    document.body.classList.toggle('vena-search-is-open', isOpen);

    if (isOpen && input) {
      window.setTimeout(() => input.focus(), 50);
    }
  }

  function resultURL(result) {
    return result.url || result.url_to_remove || '#';
  }

  function renderSearchResults(container, response, query) {
    const resources = response && response.resources && response.resources.results;
    const allResults = resources
      ? [...(resources.products || []), ...(resources.pages || []), ...(resources.articles || [])]
      : [];

    if (!query || !allResults.length) {
      container.innerHTML = query
        ? '<p class="vena-search-overlay__empty">No matching products or pages found. Try another search.</p>'
        : '';
      return;
    }

    const rows = allResults.slice(0, 5).map((result) => `
      <a class="vena-search-overlay__result" href="${escapeHTML(resultURL(result))}">
        <svg aria-hidden="true" focusable="false" viewBox="0 0 24 24" fill="none"><circle cx="11" cy="11" r="6.5" stroke="currentColor" stroke-width="1.7"></circle><path d="m16 16 4.2 4.2" stroke="currentColor" stroke-width="1.7" stroke-linecap="round"></path></svg>
        <span>${escapeHTML(result.title || result.name || '')}</span>
      </a>`).join('');

    container.innerHTML = `<p class="vena-search-overlay__result-heading">Matching results</p>${rows}`;
  }

  function initialiseHeader(root) {
    if (!root || root.dataset.venaHeaderReady === 'true') return;
    root.dataset.venaHeaderReady = 'true';

    const menuToggle = root.querySelector(SELECTORS.menuToggle);
    const drawer = root.querySelector(SELECTORS.menuDrawer);
    const scrim = root.querySelector(SELECTORS.menuScrim);
    const searchToggle = root.querySelector(SELECTORS.searchToggle);
    const overlay = root.querySelector(SELECTORS.searchOverlay);
    const closeButton = root.querySelector(SELECTORS.searchClose);
    const dialog = root.querySelector(SELECTORS.searchDialog);
    const input = root.querySelector(SELECTORS.searchInput);
    const results = root.querySelector(SELECTORS.searchResults);
    const endpoint = root.dataset.predictiveSearchEndpoint;
    const stickyPurchase = root.querySelector('[data-vena-sticky-purchase]');
    const headerShell = root.closest('.shopify-section') || root.parentElement || root;
    const buyBox = document.getElementById('buy');

    function setHeaderHidden(isHidden) {
      headerShell.classList.toggle('vena-header-shell--scroll-hidden', isHidden);
    }

    function setPurchaseVisible(isVisible) {
      if (!stickyPurchase) return;
      root.classList.toggle('vena-header--purchase-visible', isVisible);
      stickyPurchase.setAttribute('aria-hidden', isVisible ? 'false' : 'true');
    }

    function isBuyBoxAtViewportCenter() {
      if (!buyBox) return false;
      const box = buyBox.getBoundingClientRect();
      const viewportCenter = window.innerHeight / 2;
      return box.top <= viewportCenter && box.bottom >= viewportCenter;
    }

    function getDocumentScrollY() {
      return Math.max(
        0,
        window.scrollY || 0,
        window.pageYOffset || 0,
        document.documentElement?.scrollTop || 0,
        document.body?.scrollTop || 0,
        document.scrollingElement?.scrollTop || 0
      );
    }

    function updateHeaderVisibility() {
      const currentScrollY = getDocumentScrollY();
      const drawerOpen = drawer?.hasAttribute('open');
      const overlayOpen = overlay?.classList.contains('is-active');
      const hasScrolledAwayFromTop = currentScrollY > 12;
      const shouldShowPurchase = hasScrolledAwayFromTop && !drawerOpen && !overlayOpen && !isBuyBoxAtViewportCenter();

      /* The regular header stays visible. The compact row is controlled only by page position. */
      root.classList.remove('vena-header--scroll-hidden');
      setHeaderHidden(false);
      setPurchaseVisible(shouldShowPurchase);
    }

    function requestHeaderVisibilityUpdate() {
      updateHeaderVisibility();
    }

    /* Capture document scrolls as well as window scrolls: Shopify's Theme Editor preview can scroll either container. */
    window.addEventListener('scroll', requestHeaderVisibilityUpdate, { passive: true });
    document.addEventListener('scroll', requestHeaderVisibilityUpdate, { capture: true, passive: true });
    window.addEventListener('resize', requestHeaderVisibilityUpdate, { passive: true });
    document.addEventListener('visibilitychange', requestHeaderVisibilityUpdate);
    requestHeaderVisibilityUpdate();

    menuToggle?.addEventListener('click', () => {
      setHeaderHidden(false);
      setPurchaseVisible(false);
      const isOpen = drawer?.hasAttribute('open');
      setMenuState(root, !isOpen);
      requestHeaderVisibilityUpdate();
    });

    scrim?.addEventListener('click', () => {
      closeDrawer(root);
      menuToggle?.focus();
    });

    root.querySelectorAll('[data-vena-drawer-link]').forEach((link) => {
      link.addEventListener('click', () => closeDrawer(root));
    });

    searchToggle?.addEventListener('click', () => {
      if (overlay) setOverlayState(overlay, true);
      searchToggle.setAttribute('aria-expanded', 'true');
      closeDrawer(root);
    });

    closeButton?.addEventListener('click', () => {
      if (overlay) setOverlayState(overlay, false);
      searchToggle?.setAttribute('aria-expanded', 'false');
      searchToggle?.focus();
    });

    overlay?.addEventListener('click', (event) => {
      if (event.target === overlay) {
        setOverlayState(overlay, false);
        searchToggle?.setAttribute('aria-expanded', 'false');
        searchToggle?.focus();
      }
    });

    dialog?.addEventListener('keydown', (event) => {
      if (event.key !== 'Tab') return;
      const focusables = [...dialog.querySelectorAll('a[href], button:not([disabled]), input:not([disabled])')]
        .filter((element) => !element.hasAttribute('hidden'));
      if (!focusables.length) return;

      const first = focusables[0];
      const last = focusables[focusables.length - 1];
      if (event.shiftKey && document.activeElement === first) {
        event.preventDefault();
        last.focus();
      } else if (!event.shiftKey && document.activeElement === last) {
        event.preventDefault();
        first.focus();
      }
    });

    const fetchResults = debounce(async (query) => {
      if (!results || !endpoint) return;
      const cleanQuery = query.trim();
      if (cleanQuery.length < 2) {
        renderSearchResults(results, null, '');
        return;
      }

      results.innerHTML = '<p class="vena-search-overlay__empty">Searching…</p>';
      try {
        const url = new URL(endpoint, window.location.origin);
        url.searchParams.set('q', cleanQuery);
        url.searchParams.set('resources[type]', 'product,page,article');
        url.searchParams.set('resources[limit]', '5');
        url.searchParams.set('resources[options][unavailable_products]', 'hide');
        const response = await fetch(url.toString(), { headers: { Accept: 'application/json' } });
        if (!response.ok) throw new Error('Search request failed');
        renderSearchResults(results, await response.json(), cleanQuery);
      } catch (error) {
        results.innerHTML = '<p class="vena-search-overlay__empty">Search is temporarily unavailable. Press Enter to search the store.</p>';
      }
    }, 220);

    input?.addEventListener('input', (event) => fetchResults(event.target.value));

    root.querySelectorAll(SELECTORS.quickLink).forEach((link) => {
      link.addEventListener('click', () => {
        if (overlay) setOverlayState(overlay, false);
        searchToggle?.setAttribute('aria-expanded', 'false');
      });
    });

    document.addEventListener('keydown', (event) => {
      if (event.key !== 'Escape') return;

      if (overlay?.classList.contains('is-active')) {
        setOverlayState(overlay, false);
        searchToggle?.setAttribute('aria-expanded', 'false');
        searchToggle?.focus();
      }

      if (drawer?.hasAttribute('open')) {
        closeDrawer(root);
        menuToggle?.focus();
      }
    });
  }

  function initialiseAll(scope = document) {
    if (scope.matches && scope.matches(SELECTORS.root)) initialiseHeader(scope);
    scope.querySelectorAll(SELECTORS.root).forEach(initialiseHeader);
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', () => initialiseAll(document), { once: true });
  } else {
    initialiseAll(document);
  }

  document.addEventListener('shopify:section:load', (event) => initialiseAll(event.target));
})();